// Player Settings — everything about the standard player hero (LIVE / EPG / VOD). Delivery model:
// **vader admins the behavior → watch /api/config delivers it → the client pages read it.** This page only
// writes the keys it owns (PUT upserts per-key, so other settings pages are untouched):
//   • player_watch_ads: '1' (default) → watch-mode player + DESKTOP side ad rail; '0' → centered player, no ads.
//     Delivered via /api/config `player.watch_ads` → LIVE/EPG/VOD. Preview is always full-width; rail is desktop-only.
//   • live_hero_layout / live_immersive_crop: web LIVE hero PREVIEW layout (split | immersive) + crop anchor.
//     Delivered via /api/config `live.hero_layout` (keys keep the live_ prefix so the client read is unchanged).
//   • ads_live_rail_*: the 300×600 hero rail ad (zone-select or manual tag). Delivered via `ads.live_rail`,
//     rendered by <AdBanner zone="live_rail"> beside the watch-mode player.
import { useState, useEffect } from 'react'
import { Save, MonitorPlay, Megaphone } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import './LiveSettings.css'

// --- AdSpeed zone-tag helpers (mirror SiteSettings) ---
function extractServingCode(adtag) {
  if (!adtag) return ''
  if (typeof adtag === 'string') return adtag
  if (typeof adtag.ServingCode === 'string') return adtag.ServingCode
  if (typeof adtag.Zone?.ServingCode === 'string') return adtag.Zone.ServingCode
  if (adtag.Zone?.ServingCode?.['#text']) return adtag.Zone.ServingCode['#text']
  return adtag.AdTag || adtag.Tag || ''
}
function stripRefresh(tag) { return (tag || '').replace(/([?&;])refresh=\d+/gi, '').trim() }
function upgradeToHttps(tag) { return (tag || '').replace(/http:\/\//gi, 'https://') }

const DEFAULT_RAIL_TAG = '<script type="text/javascript" src="https://ads.tenband.tv/ad.php?do=js&zid=148605&oid=28740&wd=-1&ht=-1&target=_blank"></script>'
const RAIL_DEFAULTS = {
  ads_live_rail_enabled: 'true',
  ads_live_rail_mode: 'manual',
  ads_live_rail_zone_id: '',
  ads_live_rail_zone_name: '',
  ads_live_rail_tag: DEFAULT_RAIL_TAG,
  ads_live_rail_width: '300',
  ads_live_rail_height: '600',
}
const BASE_DEFAULTS = { player_watch_ads: '1', player_ad_side: 'right', player_transition_dwell_ms: '1500', live_hero_layout: 'split', live_immersive_crop: 'top', ...RAIL_DEFAULTS }
// Keys this page OWNS — only these are written on Save (PUT upserts per key; other pages' settings stay intact).
const MANAGED_KEYS = ['player_watch_ads', 'player_ad_side', 'player_transition_dwell_ms', 'live_hero_layout', 'live_immersive_crop', ...Object.keys(RAIL_DEFAULTS)]

export default function PlayerSettings() {
  const { api } = useAuth()
  const [settings, setSettings] = useState({ ...BASE_DEFAULTS })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [zones, setZones] = useState([])
  const [zonesLoading, setZonesLoading] = useState(false)
  const [resolvingTag, setResolvingTag] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    try {
      const [playerData, liveData, adsData] = await Promise.all([
        api('/settings?prefix=player_'),
        api('/settings?prefix=live_'),
        api('/settings?prefix=ads_'),
      ])
      const s = { ...(playerData.settings || {}), ...(liveData.settings || {}), ...(adsData.settings || {}) }
      setSettings(prev => ({ ...prev, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v !== null && v !== '')) }))
    } catch (e) { setStatus(`Error loading player settings: ${e.message}`) }
    setLoading(false)
  }

  async function save() {
    setSaving(true); setStatus('')
    try {
      const payload = {}
      for (const k of MANAGED_KEYS) payload[k] = String(settings[k] ?? '')
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: payload }) })
      setStatus('Player settings saved — watch delivers them to the apps within ~5 min (config cache TTL).')
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setSaving(false)
  }

  function handleChange(key, value) { setSettings(prev => ({ ...prev, [key]: value })) }

  // Lazy-load sponsor zones the first time "Select a sponsor zone" is chosen.
  useEffect(() => {
    if (settings.ads_live_rail_mode !== 'zone' || zones.length || zonesLoading) return
    setZonesLoading(true)
    api('/sponsors/zones?status=active')
      .then(d => setZones(d.zones || []))
      .catch(e => setStatus(`Error loading zones: ${e.message}`))
      .finally(() => setZonesLoading(false))
  }, [settings.ads_live_rail_mode]) // eslint-disable-line react-hooks/exhaustive-deps

  async function resolveZoneTag(zoneId, zoneName) {
    if (!zoneId) return
    setResolvingTag(true); setStatus('')
    try {
      const d = await api(`/sponsors/zones/${zoneId}/adtag?format=javascript`)
      if (d.ok === false) throw new Error(d.error?.message || 'AdSpeed returned no tag')
      const tag = upgradeToHttps(stripRefresh(extractServingCode(d.adtag)))
      if (!tag) throw new Error('Zone returned an empty tag')
      setSettings(prev => ({ ...prev, ads_live_rail_zone_id: String(zoneId), ads_live_rail_zone_name: zoneName || prev.ads_live_rail_zone_name || '', ads_live_rail_tag: tag }))
      setStatus('Zone tag fetched. Review it below, then Save.')
    } catch (e) { setStatus(`Error fetching zone tag: ${e.message}`) }
    setResolvingTag(false)
  }

  const railMode = settings.ads_live_rail_mode || 'manual'

  return (
    <div className="live-settings">
      <div className="live-settings-header">
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 4px' }}>Player Settings</h1>
        <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          The standard player hero across LIVE, EPG, and VOD — watch-mode ads, web LIVE hero layout, and the
          300×600 hero rail ad. Vader admins these; watch delivers them to the apps via /api/config.
        </p>
      </div>

      {loading ? <div style={{ padding: 24 }}>Loading…</div> : (
      <>
        {/* Watch-mode layout + web LIVE hero layout */}
        <div className="live-settings-card" style={{ maxWidth: 720 }}>
          <h3><MonitorPlay size={18} /> Watch-mode layout</h3>

          <div className="live-settings-field" style={{ marginTop: 12 }}>
            <label>Watch-mode ad rail</label>
            <select value={settings.player_watch_ads !== '0' ? 'on' : 'off'} onChange={e => handleChange('player_watch_ads', e.target.value === 'on' ? '1' : '0')} style={{ width: '100%', boxSizing: 'border-box' }}>
              <option value="on">Ads on — player + side ad rail in watch mode (current)</option>
              <option value="off">Ads off — centered player only, no ad rail</option>
            </select>
            <span className="live-settings-hint">
              When you tap Watch, the hero either keeps the player alongside the sponsor ad rail (Ads on) or shows a
              clean, centered player with no ads (Ads off). Preview mode is always full-width; the rail is
              desktop-only (portrait shows the player alone). Applies to LIVE, EPG, and VOD.
            </span>
          </div>

          <div className="live-settings-field" style={{ marginTop: 12 }}>
            <label>Watch-mode ad position</label>
            <select value={settings.player_ad_side === 'left' ? 'left' : 'right'} onChange={e => handleChange('player_ad_side', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
              <option value="right">Right — ad rail on the right of the player (default)</option>
              <option value="left">Left — ad rail on the left of the player</option>
            </select>
            <span className="live-settings-hint">
              Which side the desktop watch-mode ad rail sits on — across LIVE, EPG, and VOD. Only applies when
              Watch-mode ads are on (and on desktop; portrait always shows the player alone).
            </span>
          </div>

          <div className="live-settings-field" style={{ marginTop: 12 }}>
            <label>Transition dwell time (seconds)</label>
            <input
              type="number" min="0" max="5" step="0.1"
              value={((Number(settings.player_transition_dwell_ms) || 1500) / 1000)}
              onChange={e => { const s = Math.max(0, Math.min(5, Number(e.target.value) || 0)); handleChange('player_transition_dwell_ms', String(Math.round(s * 1000))) }}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <span className="live-settings-hint">
              How long the incoming poster (channel logo / event thumbnail) holds during a stream hand-off before a
              loading spinner appears — the smooth crossfade at every LIVE / EPG / VOD stream change. Industry-standard
              1.5s; a faster load shows no spinner at all. Range 0–5s.
            </span>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 12 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Web LIVE hero layout</h4>
            <div className="live-settings-field">
              <label>Preview layout</label>
              <select value={settings.live_hero_layout === 'immersive' ? 'immersive' : 'split'} onChange={e => handleChange('live_hero_layout', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                <option value="split">Split — centered player + rail (in preview and watch)</option>
                <option value="immersive">Immersive — full-bleed full-width cover hero in preview</option>
              </select>
              <span className="live-settings-hint">
                The web LIVE hero's PREVIEW layout. Immersive is a full-bleed cover backdrop that transitions to the
                split player+rail on Watch. (EPG/VOD are unaffected by this.)
              </span>
            </div>
            {settings.live_hero_layout === 'immersive' && (
              <div className="live-settings-field" style={{ marginTop: 10 }}>
                <label>Immersive crop</label>
                <select value={settings.live_immersive_crop === 'center' ? 'center' : 'top'} onChange={e => handleChange('live_immersive_crop', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                  <option value="top">Top-anchored — start the image from the top (EPG-like)</option>
                  <option value="center">Center — crop from the middle of the frame</option>
                </select>
                <span className="live-settings-hint">How the full-bleed immersive preview crops on desktop. (Mobile always shows the full 16:9.)</span>
              </div>
            )}
          </div>
        </div>

        {/* Hero Ads 300×600 (the rail ad) */}
        <div className="live-settings-card" style={{ maxWidth: 720, marginTop: 16 }}>
          <h3><Megaphone size={18} /> Hero Ads 300×600</h3>
          <p className="live-settings-hint" style={{ marginBottom: 14 }}>
            The 300×600 half-page ad shown beside the player in WATCH mode on desktop/tablet (when Watch-mode ads
            are on). Respects the master “Ads enabled” switch in Site Settings. Changes apply within ~5 min.
          </p>

          <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
              <input type="checkbox" checked={settings.ads_live_rail_enabled === 'true'} onChange={e => handleChange('ads_live_rail_enabled', e.target.checked ? 'true' : 'false')} style={{ margin: 0, width: 16, height: 16 }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Hero rail zone</span>
              <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>The 300×600 rail (desktop/tablet)</span>
            </label>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Ad source</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ value: 'manual', label: 'Enter URL / tag manually' }, { value: 'zone', label: 'Select a sponsor zone' }].map(opt => {
                  const active = railMode === opt.value
                  return (
                    <label key={opt.value} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 12px', borderRadius: 6, fontSize: 13, background: active ? 'rgba(59,130,246,0.1)' : 'transparent', border: `1px solid ${active ? 'rgba(59,130,246,0.3)' : 'var(--color-border)'}` }}>
                      <input type="radio" name="ads_live_rail_mode" value={opt.value} checked={active} onChange={e => handleChange('ads_live_rail_mode', e.target.value)} style={{ margin: 0 }} />
                      {opt.label}
                    </label>
                  )
                })}
              </div>
            </div>

            {railMode === 'zone' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Sponsor zone</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select value={settings.ads_live_rail_zone_id || ''} disabled={zonesLoading} onChange={e => { const id = e.target.value; const z = zones.find(z => String(z.id ?? z.ZoneId) === id); handleChange('ads_live_rail_zone_id', id); handleChange('ads_live_rail_zone_name', z ? String(z.name ?? z.Name ?? '') : '') }} style={{ flex: 1, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                    <option value="">{zonesLoading ? 'Loading zones…' : '— Select a zone —'}</option>
                    {zones.map(z => { const id = String(z.id ?? z.ZoneId); return <option key={id} value={id}>{(z.name ?? z.Name ?? `Zone ${id}`)} (#{id})</option> })}
                  </select>
                  <button type="button" className="btn-ghost" disabled={!settings.ads_live_rail_zone_id || resolvingTag} onClick={() => resolveZoneTag(settings.ads_live_rail_zone_id, settings.ads_live_rail_zone_name)}>{resolvingTag ? 'Fetching…' : 'Fetch tag'}</button>
                </div>
                <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '6px 0 0', lineHeight: 1.4 }}>Fetches the zone's JavaScript ad tag (e.g. the “tbtv-web-vertical” 300×600 zone). JS tags only — VAST/IFrame zone formats are not supported here.</p>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{railMode === 'zone' ? 'Resolved ad tag' : 'Ad tag / URL'}</label>
              <textarea value={settings.ads_live_rail_tag} readOnly={railMode === 'zone'} onChange={e => handleChange('ads_live_rail_tag', e.target.value)} rows={3} placeholder={'<script type="text/javascript" src="https://ads.tenband.tv/ad.php?do=js&zid=…&oid=28740&wd=-1&ht=-1&target=_blank"></script>'} style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box', opacity: railMode === 'zone' ? 0.8 : 1 }} />
              <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0', lineHeight: 1.4 }}>Empty tag → the rail renders nothing (no generic fallback). Fixed 300×600. Do not add a refresh param — refresh is handled client-side.</p>
            </div>
          </div>
        </div>

        <div className="live-settings-actions" style={{ maxWidth: 720 }}>
          <button className="btn-primary" disabled={saving} onClick={save}><Save size={15} /> {saving ? 'Saving…' : 'Save player settings'}</button>
        </div>
        {status && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--color-text-secondary)', maxWidth: 720 }}>{status}</div>}
      </>
      )}
    </div>
  )
}
